import { AppBreadcrumb, AppCard, AppContainer } from '@/components/atoms';
import CreateMassagerForm, {
  type CreateMassagerFormData,
} from '@/pages/admin/admin-massagers/CreateMassagerForm';
import type { CreateMassagerPayload } from '@/services/admin/massagers';
import { useAdminClinic } from '@/hooks/admin/useAdminClinics';
import { useAdminCreateMassager } from '@/hooks/admin/useAdminMassagers';
import useAppToast from '@/hooks/useAppToast';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getClinicOptions } from '@/services/admin/clinics';

import { dateValueToIso } from '@/utils/dateConverter';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';

function resolveServiceTypeForCreate(
  clinicServiceType: 'A2' | 'R2' | 'BOTH',
  data: CreateMassagerFormData
): 'A2' | 'R2' | 'BOTH' {
  if (clinicServiceType !== SERVICE_TYPE_BOTH) return clinicServiceType;

  const hasA2License =
    (data.acupuncture_license_number ?? '').toString().trim() !== '' ||
    (data.moxibustion_license_number ?? '').toString().trim() !== '';
  const hasR2License = (data.license_number ?? '').toString().trim() !== '';

  if (hasA2License && hasR2License) return SERVICE_TYPE_BOTH;
  if (hasA2License) return SERVICE_TYPE_A2;
  if (hasR2License) return SERVICE_TYPE_R2;
  return SERVICE_TYPE_BOTH;
}

const CreateMassagerPage: React.FC = () => {
  const toast = useAppToast();
  const navigate = useNavigate();
  const { site } = useParams<{ site?: string }>();
  const [searchParams] = useSearchParams();
  const clinicIdFromQuery = searchParams.get('clinic_id');
  const clinicIdNumber = clinicIdFromQuery ? parseInt(clinicIdFromQuery, 10) : null;

  const { createMassagerMutation, isLoading } = useAdminCreateMassager();
  const [selectedClinicIdForLocations, setSelectedClinicIdForLocations] = useState(
    clinicIdNumber ?? 0
  );

  useEffect(() => {
    if (clinicIdNumber != null) {
      setSelectedClinicIdForLocations(clinicIdNumber);
    }
  }, [clinicIdNumber]);

  const siteForBreadcrumb = site ?? 'medix';

  // Fetch clinic options with latest name from clinic_informations (fallback to clinic_number)
  const {
    data: clinicOptionsData,
    isLoading: isClinicsLoading,
  } = useQuery({
    queryKey: ['admin', 'clinics', 'options', site],
    queryFn: () => getClinicOptions(site ?? 'medix'),
    enabled: !!site,
  });
  const clinicIdForLocations =
    clinicIdNumber ?? selectedClinicIdForLocations;
  const { data: singleClinicData } = useAdminClinic(site ?? '', clinicIdForLocations);

  const clinicOptions = useMemo(() => {
    const list = clinicOptionsData ?? [];
    // For 治療院ID select, show latest clinic_information.name if exists, else clinic_number
    return list.map(c => ({
      id: c.id,
      clinic_number: c.name, // MassagerClinicSelect uses clinic_number as display label
    }));
  }, [clinicOptionsData]);

  const clinicLocationOptions = useMemo(() => {
    const data = singleClinicData?.data?.data ?? singleClinicData?.data;
    const locations = data?.clinic_locations ?? [];
    return Array.isArray(locations) ? locations : [];
  }, [singleClinicData]);
  const clinicSiteType = useMemo(() => {
    const data = singleClinicData?.data?.data ?? singleClinicData?.data;
    return (data?.site_type ?? null) as string | null;
  }, [singleClinicData]);

  // Determine if we can safely show 「詳細画面へ戻る」 (only when clinic_id from URL exists in DB).
  const hasValidClinicForBackButton =
    clinicIdNumber != null &&
    !!((singleClinicData?.data as any)?.clinic_number ?? (singleClinicData?.data as any)?.data?.clinic_number);

  /** A2 | R2 | BOTH: prefer clinic.service_type. When unknown, default BOTH so both A2/R2 are enabled. */
  const clinicServiceType = useMemo((): 'A2' | 'R2' | 'BOTH' => {
    const clinicRaw = (singleClinicData?.data?.data ?? singleClinicData?.data)?.service_type;
    const s = (clinicRaw ?? '').toString().toUpperCase();
    if (!s) return SERVICE_TYPE_BOTH;
    if (s === SERVICE_TYPE_R2) return SERVICE_TYPE_R2;
    if (s === SERVICE_TYPE_BOTH) return SERVICE_TYPE_BOTH;
    return SERVICE_TYPE_A2;
  }, [singleClinicData]);

  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <Link to={`/${siteForBreadcrumb}/admin`} style={{ color: 'inherit' }}>
            Home
          </Link>
        ),
      },
      {
        title:
          clinicIdForLocations && clinicIdForLocations > 0 ? (
            <Link
              to={`/${siteForBreadcrumb}/admin/clinics/${clinicIdForLocations}`}
              style={{ color: 'inherit' }}
            >
              治療院詳細
            </Link>
          ) : (
            '治療院詳細'
          ),
      },
      { title: '新規' },
    ],
    [siteForBreadcrumb, clinicIdForLocations]
  );

  const handleSubmit = useCallback(
    async (data: CreateMassagerFormData): Promise<void> => {
      if (!site) {
        toast.error('サイト情報が取得できませんでした。');
        return;
      }
      // Prefer clinic_id from URL query (clinic_id=xxx). If not present, fall back to the form value.
      const effectiveClinicId = clinicIdNumber ?? data.clinic_id;
      const effectiveServiceType = resolveServiceTypeForCreate(clinicServiceType, data);
      const isR2 = effectiveServiceType === SERVICE_TYPE_R2;
      const isBoth = effectiveServiceType === SERVICE_TYPE_BOTH;
      const wideAreaRowsA2 = (data.wide_area_union_rows ?? []).filter(
        r => (r.wide_area_union_code ?? '').trim() !== '' && (r.wide_area_union_number ?? '').trim() !== ''
      );
      const wideAreaRowsR2 = (data.massage_wide_area_union_rows ?? []).filter(
        r => (r.wide_area_union_code ?? '').trim() !== '' && (r.wide_area_union_number ?? '').trim() !== ''
      );

      const toLocationPayload = (
        rows: Array<{
          clinic_location_id?: number | null;
          available_from?: string | null;
          available_to?: string | null;
          regist_date?: string | null;
          stop_request_date?: string | null;
        }>
      ) =>
        rows
          .filter(r => (r?.clinic_location_id ?? 0) > 0)
          .map(r => ({
            id: r.clinic_location_id!,
            available_from: dateValueToIso(r.available_from ?? null),
            available_to: dateValueToIso(r.available_to ?? null),
            regist_date: dateValueToIso(r.regist_date ?? null),
            stop_request_date: dateValueToIso(r.stop_request_date ?? null),
          }));

      const payload: Record<string, unknown> = {
        clinic_id: effectiveClinicId,
        service_type: effectiveServiceType,
        name: data.name,
        name_kana: data.name_kana ?? null,
        login_id: data.login_id ?? null,
        password: data.password ?? null,
        email: data.email ?? null,
        wide_area_union_code: data.wide_area_union_code ?? null,
        wide_area_union_number: data.wide_area_union_number ?? null,
        prefecture_number: data.prefecture_number ?? null,
      };

      // Only send clinic_location_ids_* for the active service side (objects: id, available_from, available_to)
      if (!isR2) {
        payload.clinic_location_ids_a2 = toLocationPayload(data.clinic_locations_a2 ?? []);
      }
      if (isR2 || isBoth) {
        payload.clinic_location_ids_r2 = toLocationPayload(data.clinic_locations_r2 ?? []);
      }

      if (!isR2 || isBoth) {
        payload.wide_area_union_rows = wideAreaRowsA2.length ? wideAreaRowsA2 : undefined;
      }
      if (isR2 || isBoth) {
        payload.massage_wide_area_union_rows = wideAreaRowsR2.length ? wideAreaRowsR2 : undefined;
        // Backend expects massage_license_number (not license_number)
        payload.massage_license_number = data.license_number ?? null;
      }
      if (!isR2 || isBoth) {
        payload.acupuncture_license_number = data.acupuncture_license_number ?? null;
        payload.moxibustion_license_number = data.moxibustion_license_number ?? null;
      }

      try {
        const result = await createMassagerMutation({
          site,
          data: payload as unknown as CreateMassagerPayload,
        });
        if (result?.success) {
          const newId = result?.data?.id;
          if (site && newId != null) {
            navigate(`/${site}/admin/massagers/${newId}/edit`, { replace: true });
          } else {
            navigate(`/${site}/admin/clinics/${effectiveClinicId}`, { replace: true });
          }
        }
      } catch (error) {
        // Special case: clinic_id refers to deleted / non-existent clinic
        // or out-of-scope clinic (tenant access error).
        const err = error as import('axios').AxiosError<any>;
        const dataErr = err.response?.data as any;
        const clinicIdErrors: string[] | undefined = dataErr?.errors?.clinic_id;
        const clinicNotFoundMsg =
          '指定された治療院は存在しません。他の治療院を選んでください。';
        const message = (dataErr?.message ?? '').toString().trim();

        if (
          (Array.isArray(clinicIdErrors) && clinicIdErrors.includes(clinicNotFoundMsg)) ||
          message === clinicNotFoundMsg
        ) {
          // Toast is already shown in hook; just redirect to plain create URL (no clinic_id param)
          navigate(`/${siteForBreadcrumb}/admin/massagers/create`, { replace: true });
        }
      }
    },
    [site, clinicServiceType, createMassagerMutation, navigate, toast, siteForBreadcrumb]
  );

  if (isClinicsLoading) {
    return (
      <AppContainer className="flex min-h-[300px] items-center justify-center">
        <Spin size="large" />
      </AppContainer>
    );
  }

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} showHome={false} />
      <AppCard title="施術者作成" className="w-full">
        <CreateMassagerForm
          clinicOptions={clinicOptions}
          clinicLocationOptions={clinicLocationOptions}
          initialClinicId={clinicIdNumber}
          fixedClinicId={clinicIdNumber}
          initialValues={undefined}
          clinicServiceType={clinicServiceType}
          siteType={clinicSiteType}
          onClinicIdChange={setSelectedClinicIdForLocations}
          onSubmit={handleSubmit}
          onCancel={
            hasValidClinicForBackButton && clinicIdNumber != null
              ? () => navigate(`/${siteForBreadcrumb}/admin/clinics/${clinicIdNumber}`)
              : undefined
          }
          isLoading={isLoading}
        />
      </AppCard>
    </AppContainer>
  );
};

export default CreateMassagerPage;
